'use client';

/**
 * ExtractionSourceBadge — compact "TV" / "GV" badge indicating which OCR/vision
 * provider produced a document's AI extraction.
 *
 * - "TV" = Total Vision (self-hosted Ollama OCR, preferred path)
 * - "GV" = Gemini Vision (cloud fallback)
 *
 * Designed for internal troubleshooting: clients don't need to know what the
 * labels mean, but they let the support team quickly identify which provider
 * produced a result when investigating extraction issues.
 *
 * The badge is intentionally small and muted so it doesn't distract from the
 * primary confidence indicator. It accepts either a raw `source` string
 * ('total-vision' | 'gemini') or a `customFields` object from which the
 * provenance is resolved via `getExtractionProvenance`.
 */

import React from 'react';
import {
  getExtractionProvenance,
  getExtractionSourceBadge,
  type ExtractionSource,
} from '@/lib/finance/inboxShared';

export interface ExtractionSourceBadgeProps {
  /** Direct extraction source value (takes precedence over customFields). */
  source?: ExtractionSource | string | null;
  /** Optional model name for the tooltip (e.g. "glm-ocr", "gemini-2.5-flash"). */
  model?: string | null;
  /** Work item custom_fields — provenance is resolved from here when `source` is not provided. */
  customFields?: Record<string, unknown> | null;
  /** Optional extra className for the badge span. */
  className?: string;
  /** When true, renders a slightly larger badge (used in modal headers). */
  size?: 'sm' | 'md';
}

export function ExtractionSourceBadge({
  source,
  model,
  customFields,
  className = '',
  size = 'sm',
}: ExtractionSourceBadgeProps) {
  // Resolve the source: explicit prop takes precedence, otherwise resolve from customFields
  let resolvedSource: ExtractionSource | null = null;
  let resolvedModel: string | null = model ?? null;

  if (source) {
    const s = String(source).toLowerCase().trim();
    if (s === 'total-vision' || s === 'total_vision' || s === 'tv') {
      resolvedSource = 'total-vision';
    } else if (s === 'gemini' || s === 'gv') {
      resolvedSource = 'gemini';
    }
  }

  if (!resolvedSource && customFields) {
    const provenance = getExtractionProvenance(customFields);
    resolvedSource = provenance.source;
    if (!resolvedModel) resolvedModel = provenance.model;
  }

  const badge = getExtractionSourceBadge(resolvedSource, resolvedModel);
  if (!badge) return null;

  const sizeCls = size === 'md'
    ? 'px-1.5 py-0.5 text-[11px]'
    : 'px-1 py-0.5 text-[10px]';

  return (
    <span
      className={`inline-flex items-center rounded font-semibold leading-none ${sizeCls} ${badge.cls} ${className}`}
      title={badge.title}
    >
      {badge.label}
    </span>
  );
}

export default ExtractionSourceBadge;
